import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IconArrowUp, IconStop } from '@zoom/icons-react'
import {
  generateTextToImages,
  resetAddImage,
  resetTextToImage,
} from '../vb/actions'
import {
  selectQuery,
  selectRecommendation,
  selectArtisticStyle,
  selectSpace,
} from './reducer'
import {
  setResultCleared,
  setErrorMessage,
  setIsNotificationVisible,
  updateIsTryAnotherTextVisible,
  updateQuery,
  updateRecommendation,
} from './actions'
import ErrorMessage from './ErrorMessage'
import {
  ADD_DETAILS_OPTIONAL,
  DESCRIBE_A_BACKGROUND_IMAGE,
  TEXT_LIMIT_HAS_BEEN_REACHED,
} from './translation/resources'
import { selectTextToImagesLoading } from '../vb/reducer'
import { selectZaConfig } from '../root/reducer'
import { logGenerateButtonClicked } from '../telemetry/telemetry'
import './NewInputBox.css'

const INPUT_VALUE_MAX_LENGTH = 240
const INPUT_VISIBLE_LINE_COUNT = 4
const INPUT_LINE_HEIGHT = 18
const INPUT_VERTICAL_PADDING = 12
const INPUT_MAX_HEIGHT =
  INPUT_VISIBLE_LINE_COUNT * INPUT_LINE_HEIGHT + INPUT_VERTICAL_PADDING

const NewInputBox = ({ setGeneratedImagesKey }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const inputRef = useRef(null)
  const [isTextLimitReached, setIsTextLimitReached] = useState(false)
  const query = useSelector(selectQuery)
  const isTextToImagesLoading = useSelector(selectTextToImagesLoading)
  const zaConfigOutput = useSelector(selectZaConfig)
  const cleanedQuery = !query ? null : query.trim()
  const selectedRecommendation = useSelector(selectRecommendation)
  const selectedArtisticStyle = useSelector(selectArtisticStyle)
  const selectedSpace = useSelector(selectSpace)
  const inputPlaceholder = selectedSpace
    ? ADD_DETAILS_OPTIONAL
    : DESCRIBE_A_BACKGROUND_IMAGE
  const hasPromptInput = Boolean(
    cleanedQuery || selectedRecommendation || selectedSpace
  )
  const isMobileDevice = zaConfigOutput?.product === 'mobile'
  const isGenerateDisabled = !isTextToImagesLoading && !hasPromptInput

  const autoResize = () => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, INPUT_MAX_HEIGHT) + 'px'
    el.style.overflowY =
      el.scrollHeight > INPUT_MAX_HEIGHT ? 'auto' : 'hidden'
  }

  useEffect(() => {
    if (!isMobileDevice) {
      inputRef.current?.focus()
    }
    autoResize()
  }, [isMobileDevice])

  useEffect(() => {
    autoResize()
  }, [query])

  const handleInputChange = (e) => {
    let { value } = e.target
    const isOverTextLimit = value.length >= INPUT_VALUE_MAX_LENGTH

    if (isOverTextLimit) {
      value = value.slice(0, INPUT_VALUE_MAX_LENGTH)
    }

    setIsTextLimitReached(isOverTextLimit)
    dispatch(updateQuery(value))
    dispatch(updateIsTryAnotherTextVisible(false))
    autoResize()

    if (!value || value.indexOf(selectedRecommendation) === -1) {
      dispatch(updateRecommendation(null))
    }
  }

  const handleGenerate = (e) => {
    e.preventDefault()
    if (hasPromptInput) {
      const styles = selectedArtisticStyle
        ? ` --style=${selectedArtisticStyle}`
        : ''
      const spaces = selectedSpace ? ` --space=${selectedSpace}` : ''
      const basePrompt = cleanedQuery || selectedRecommendation || ''
      const finalPrompt = basePrompt + styles + spaces

      // console.log('finalPrompt:', finalPrompt)

      if (!isMobileDevice) {
        inputRef.current?.focus()
      }
      dispatch(resetTextToImage())
      setGeneratedImagesKey((prevKey) => prevKey + 1)
      dispatch(generateTextToImages({ query: finalPrompt }))
      dispatch(updateIsTryAnotherTextVisible(true))
      dispatch(setErrorMessage(null))
      dispatch(setIsNotificationVisible(false))
      dispatch(resetAddImage())
      dispatch(setResultCleared(false))
      logGenerateButtonClicked()
    }
  }

  const handleStopGenerate = (e) => {
    e.preventDefault()
    dispatch(resetTextToImage())
    dispatch(updateIsTryAnotherTextVisible(false))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (isTextToImagesLoading) {
        handleStopGenerate(e)
      } else {
        handleGenerate(e)
      }
    }
  }

  return (
    <div className="new-input-box">
      <div className="new-input-box-field">
        {!query && (
          <span className="new-input-box-placeholder" aria-hidden="true">
            {t(inputPlaceholder)}
          </span>
        )}
        <textarea
          id="input-box"
          ref={inputRef}
          rows={1}
          className="new-input-box-input"
          value={query || ''}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder=""
          aria-label="Describe a virtual background"
          maxLength={INPUT_VALUE_MAX_LENGTH}
        />
        <button
          className={`new-input-box-submit${isGenerateDisabled ? ' disabled' : ''}`}
          onClick={isTextToImagesLoading ? handleStopGenerate : handleGenerate}
          disabled={isGenerateDisabled}
          aria-label={t(isTextToImagesLoading ? 'Stop' : 'Generate')}
          type="button"
        >
          {isTextToImagesLoading ? (
            <IconStop style={{ color: '#fff' }} />
          ) : (
            <IconArrowUp style={{ color: '#fff' }} />
          )}
        </button>
      </div>
      {isTextLimitReached && (
        <p className="new-input-box-limit-message">
          {t(TEXT_LIMIT_HAS_BEEN_REACHED)}
        </p>
      )}
      <ErrorMessage />
    </div>
  )
}

export default NewInputBox
